/**
 * i18n — one instance, two audiences.
 *
 * The app is a hybrid: the game UI is Vue (rooted at `HeroScreen`), but the
 * landing, the legal overlays and the in-game admin panel are vanilla TS. So
 * this module exports BOTH the plugin (installed in `bootstrap.ts`) and a plain
 * `t()` that vanilla modules import directly. Same keys, same catalogue, one
 * source of truth — a key never means two different things.
 *
 * Message sources, merged in this order (later wins):
 *   1. `locales/<id>.json` — the baseline, committed to git.
 *   2. `GET /api/i18n/<id>` — admin overrides from the `translations` table.
 *
 * `en_US` is bundled eagerly because it is the fallback: if a lazy chunk or the
 * override request fails, the UI must still be readable rather than showing raw
 * dotted keys. Everything else is a lazy chunk.
 */
import { createI18n } from 'vue-i18n';
import { api } from '../../core/network/api';
import {
  DEFAULT_LOCALE, isLocaleId, localeTag, detectLocale,
  type LocaleId,
} from './locales';
import enUS from './locales/en_US.json';

/** Where a guest's pick is remembered (tokens never go here — copy is not a secret). */
const STORAGE_KEY = 'caligo:locale';

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  // Missing keys already fall back to en_US; the console warnings on top of that
  // are noise in dev and tell us nothing the fallback hasn't handled.
  missingWarn: false,
  fallbackWarn: false,
  messages: { [DEFAULT_LOCALE]: enUS },
});

/**
 * Translate. Safe to call from vanilla TS and from SFC setup alike.
 * Kept as a thin delegate rather than a re-export so the reactive locale on
 * `i18n.global` is read at call time, not captured once at module load.
 */
export const t = ((key: string, named?: Record<string, unknown>) =>
  named ? i18n.global.t(key, named) : i18n.global.t(key)) as {
    (key: string): string;
    (key: string, named: Record<string, unknown>): string;
  };

/** Pluralised translate — `t('x.bouts', 3)` picks the plural form for the locale. */
export const tc = (key: string, count: number, named?: Record<string, unknown>) =>
  i18n.global.t(key, { n: count, count, ...named }, count);

/** Lazy baseline chunks, one per locale file. */
const baselines = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*.json');

const loaded = new Set<LocaleId>([DEFAULT_LOCALE]);

/** Admin overrides for one locale. Never throws — a dead endpoint just means no overrides. */
async function fetchOverrides(id: LocaleId): Promise<Record<string, unknown>> {
  try {
    const res = await api.get<{ locale: string; messages: Record<string, unknown> }>(
      `/i18n/${id}`,
    );
    return res.messages ?? {};
  } catch {
    return {};
  }
}

/**
 * Deep-merge overrides onto a baseline. Overrides arrive as a nested object
 * mirroring the JSON shape, so a partial branch patches only its own leaves.
 */
function merge(
  base: Record<string, unknown>,
  over: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    const prev = out[k];
    out[k] = v !== null && typeof v === 'object' && !Array.isArray(v)
        && prev !== null && typeof prev === 'object' && !Array.isArray(prev)
      ? merge(prev as Record<string, unknown>, v as Record<string, unknown>)
      : v;
  }
  return out;
}

/** Ensure a locale's messages are in the instance. Idempotent. */
export async function loadLocale(id: LocaleId): Promise<void> {
  if (loaded.has(id)) return;

  const loader = baselines[`./locales/${id}.json`];
  let messages: Record<string, unknown> = {};
  if (loader) {
    try {
      messages = (await loader()).default;
    } catch {
      // Chunk failed (offline, bad deploy) — fall through with an empty
      // baseline; vue-i18n then falls back to en_US per key.
    }
  }

  i18n.global.setLocaleMessage(id, merge(messages, await fetchOverrides(id)) as never);
  loaded.add(id);
}

/** Subscribers that must repaint by hand — the vanilla screens have no reactivity. */
const listeners = new Set<(id: LocaleId) => void>();

/** Register a repaint callback. Returns an unsubscribe. */
export function onLocaleChange(fn: (id: LocaleId) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Switch tongues: load the catalogue, flip the instance, update `<html lang>`
 * (screen readers and CSS hyphenation both read it), remember the choice, and
 * poke the vanilla screens.
 */
let switchSeq = 0;

export async function setLocale(id: LocaleId): Promise<void> {
  // Latest choice wins: a tap on a tongue still loading, then a tap on a cached
  // one, must not let the slow load land last and flip the screen back.
  const mine = ++switchSeq;
  await loadLocale(id);
  if (mine !== switchSeq) return;
  i18n.global.locale.value = id;
  document.documentElement.lang = localeTag(id);
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Private-mode / storage-disabled — the choice just won't survive a reload.
  }
  listeners.forEach((fn) => fn(id));
}

export function activeLocale(): LocaleId {
  return i18n.global.locale.value as LocaleId;
}

/** The remembered pick, if there is a valid one. */
export function storedLocale(): LocaleId | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocaleId(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Pre-auth boot: honour a remembered pick, else the browser's languages.
 * Called before the first paint so a returning German visitor never sees a
 * flash of English. The authenticated preference is applied later, once
 * `/account/me` has answered — see `applyAccountLocale`.
 */
export async function initLocale(): Promise<void> {
  await setLocale(storedLocale() ?? detectLocale());
}

/**
 * Post-auth reconciliation. The server-side preference wins when it exists —
 * it is the choice the player made deliberately on the Oath Book, and it should
 * follow them across devices. When the account has none yet, the guest's
 * current pick is adopted and handed back so the caller can persist it.
 */
export async function applyAccountLocale(
  accountLocale: string | null | undefined,
): Promise<{ adopt: LocaleId | null }> {
  if (isLocaleId(accountLocale)) {
    if (accountLocale !== activeLocale()) await setLocale(accountLocale);
    return { adopt: null };
  }
  return { adopt: activeLocale() };
}

export { LOCALES, CURRENCIES, DEFAULT_LOCALE, localeTag, isLocaleId, isCurrencyCode } from './locales';
export type { LocaleId, CurrencyCode } from './locales';
